/**
 * Bizy-led "claim your first reward" onboarding step (RR-72). Picks up where the
 * trial welcome recognition demo (RR-14) leaves off: once Bizy has given the
 * founding admin +10 points, Bizy offers to walk them through redeeming a
 * post-wrap with those points, then guides them on the Rewards page.
 *
 * Gated behind `plg-bizy-led-onboarding`, trial-only, and (for the flight
 * choreography) desktop + full motion — mirroring the recognition demo.
 */

/**
 * URL param that carries the redeem-demo intent from the homepage offer to
 * `/rewards` across an SPA navigation (so the Bizy flight overlay, which lives in
 * `AppLayout`, stays mounted). A full reload would reset the flight.
 */
export const REDEEM_DEMO_PARAM = 'bizyRedeem';
export const REDEEM_DEMO_PARAM_VALUE = '1';

/**
 * When present with `bizyRedeem=1`, the Rewards-page demo ignores the
 * `first_redeem_done` dismissal for this visit — used by the homepage
 * `?bizyOffer=1` iteration shortcut so you can re-run without a Rails reset.
 */
export const REDEEM_DEMO_FORCE_PARAM = 'bizyForce';
export const REDEEM_DEMO_FORCE_PARAM_VALUE = '1';

/**
 * Dev/iteration shortcut: open the homepage redeem offer without replaying the
 * recognition demo. Visit `/` (or the homepage) with `?bizyOffer=1`. Stripped
 * from the URL on open; bypasses the `first_redeem_done` dismissal for that
 * session so you don't need a Rails reset between tweaks.
 */
export const REDEEM_OFFER_PARAM = 'bizyOffer';
export const REDEEM_OFFER_PARAM_VALUE = '1';

/**
 * Server-persisted flag marking that the founding admin has been guided through
 * (or has declined) their first redemption. Once set, neither the offer nor the
 * Rewards-page auto-launch fire again — so a real, irreversible redemption can't
 * be re-triggered.
 */
export const REDEEM_DEMO_DISMISSAL = { namespace: 'rewards_demo', name: 'first_redeem_done' } as const;

/**
 * Min viewport width for the guided flight, mirroring the recognition demo
 * (`RECOGNITION_DEMO_MIN_WIDTH_PX`). Below this we skip the flight and just
 * deep-link the person to the post-wrap modal.
 */
export const REDEEM_DEMO_MIN_WIDTH_PX = 1240;

/**
 * Beat after the welcome demo closes before Bizy re-enters with the redeem offer.
 * Long enough for fly-home to finish and the welcome post's reveal + shimmer to
 * settle so the eye is on the feed before she chimes in.
 */
export const REDEEM_OFFER_DELAY_MS = 3200;

/**
 * Marks the founding admin's welcome recognition in the feed so the redeem offer
 * can park Bizy beside it. Set on a wrapper in `RecognitionFeedList`.
 */
export const REDEEM_WELCOME_POST_ATTR = 'data-bizy-welcome-post';

/**
 * Inline Bizy copy. Kept inline (like `trial-welcome.ts`) rather than i18n while
 * the flow is an experiment behind a flag; it can move to locale files when the
 * experiment graduates.
 */
export const REDEEM_OFFER_MESSAGE = 'I just gave you 10 points! Want to see how to use them?';
export const REDEEM_OFFER_CONFIRM = "Let's go!";
export const REDEEM_OFFER_DISMISS = 'Skip';

/** Bizy's nudge once the post-wrap modal is open and she's parked beside it. */
export const REDEEM_COACH_ARRIVAL = "We made it! Pick out a pattern and color you like — this one's on your 10 points.";

/**
 * Bizy's send-off beside the post-purchase confirmation after they redeem.
 * Tees up the next onboarding step (inviting teammates) — nobody's around to
 * recognize yet on a brand-new company.
 */
export const REDEEM_SUCCESS_MESSAGE =
	"YESSS — five post wraps, all yours! Next up: inviting your teammates, so you have someone to wrap. Find me in the sidebar whenever you're ready.";

/**
 * Specific quips keyed by the pattern/color values from
 * `post-wrap-customizer.tsx`. Generic quips are periodically mixed in and are
 * also used as a fallback for anything unmapped.
 */
export const REDEEM_PATTERN_QUIPS: Record<string, string> = {
	stripes: 'Stripes! Nice and classic. Bees have known this for years.',
	dots: 'Dots! Like confetti that agreed to stay put.',
	ripple: 'Ripple! For gratitude that makes waves.',
	plaid: 'Plaid?! Cozy cabin vibes — if the cabin ran entirely on neon.',
	avatar: "Too much? I don't think so.",
	custom: 'A custom upload — an artist in the making. Show-off.',
	popsicle: 'Popsicle. Refreshing, and zero brain freeze. Solid pick.',
	pride: 'Rainbow! Instantly 10% more fabulous.',
};

export const REDEEM_COLOR_QUIPS: Record<string, string> = {
	green: 'Green — the color of go. Full send.',
	purple: 'Purple: the royal treatment. Your posts deserve a crown.',
	orange: 'Orange! Subtlety has left the chat.',
	teal: "Teal! Officially my favorite. Don't tell the others.",
	pink: 'Pink! Bold, confident, slightly flamingo.',
	dark: "Dark plaid. Serious business. It's giving lumberjack.",
	light: 'Light plaid. Cozy, polished, owns at least one good candle.',
};

export const REDEEM_GENERIC_QUIPS = [
	"Ooh, that's the one. Or is it?",
	'Yes. Maybe. Definitely maybe.',
	'Strong choice. No notes. Several opinions, but no notes.',
] as const;

/**
 * Occasionally swapped in on color picks (see REDEEM_BEE_LAMENT_CHANCE) —
 * Bizy grumbling that there's still no bee-themed post wrap.
 */
export const REDEEM_BEE_LAMENTS = [
	'Lovely. Still no bee-themed post wrap, though. I have filed a formal complaint.',
	'You know what color would really pop? Yellow with black stripes. Just saying.',
	'Another beautiful option that is inexplicably not bee-themed.',
	'No bee pattern yet. I remain brave during this difficult time.',
] as const;

/** Chance a color pick triggers a bee lament instead of the color quip. */
export const REDEEM_BEE_LAMENT_CHANCE = 0.2;
